/* eslint-disable react-refresh/only-export-components */
/* eslint-disable no-empty */
/* eslint-disable @typescript-eslint/no-unused-expressions */
import type { PlatformLoginResponse } from '@/types/platform'
import type { Reducer } from 'react'
import React, { useContext, useEffect, useMemo, useReducer } from 'react'

type State = {
  platformUserInfo: PlatformLoginResponse | null
}

const safeParse = <T,>(key: string, fallback: T): T => {
  try {
    const raw = localStorage.getItem(key)
    if (!raw) return fallback
    const parsed = JSON.parse(raw)
    return parsed
  } catch {
    return fallback
  }
}

const initialState: State = {
  platformUserInfo: safeParse<PlatformLoginResponse | null>(
    'platformUserInfo',
    null
  ),
}

type Action =
  | { type: 'PLATFORM_SIGNIN'; payload: PlatformLoginResponse }
  | { type: 'PLATFORM_SIGNOUT' }

function platformUserInfoReducer(
  state: PlatformLoginResponse | null,
  action: Action
) {
  switch (action.type) {
    case 'PLATFORM_SIGNIN':
      return action.payload
    case 'PLATFORM_SIGNOUT':
      return null
    default:
      return state
  }
}

const rootReducer: Reducer<State, Action> = (state, action) => {
  if (action.type === 'PLATFORM_SIGNOUT') {
    localStorage.removeItem('platformUserInfo')
    return { ...state, platformUserInfo: null }
  }

  return {
    platformUserInfo: platformUserInfoReducer(state.platformUserInfo, action),
  }
}

type StoreContext = {
  state: State
  dispatch: React.Dispatch<Action>
}

const Store = React.createContext<StoreContext | undefined>(undefined)

function PlatformStoreProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(rootReducer, initialState)

  useEffect(() => {
    try {
      state.platformUserInfo
        ? localStorage.setItem(
            'platformUserInfo',
            JSON.stringify(state.platformUserInfo)
          )
        : localStorage.removeItem('platformUserInfo')
    } catch {}
  }, [state.platformUserInfo])

  const value = useMemo(() => ({ state, dispatch }), [state, dispatch])
  return <Store.Provider value={value}>{children}</Store.Provider>
}

function usePlatformStore() {
  const ctx = useContext(Store)
  if (!ctx)
    throw new Error(
      'usePlatformStore mest be used within <PlatformStoreProvider>'
    )
  return ctx
}

export { Store, PlatformStoreProvider, usePlatformStore }
